
import React, { useState, useEffect } from 'react';
import { OrderItem } from '@/types';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Trash2, Plus, Minus, FileText } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import PDVDanfeViewer from './PDVDanfeViewer';
import { useDanfe } from '@/hooks/use-danfe';
import { useToast } from '@/hooks/use-toast';

interface PDVCartProps {
  items: OrderItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  showDanfe?: boolean;
  orderId?: number;
  onDanfeClose?: () => void;
}

const PDVCart: React.FC<PDVCartProps> = ({ 
  items, 
  onUpdateQuantity, 
  onRemoveItem,
  showDanfe = false,
  orderId = null,
  onDanfeClose
}) => {
  const { 
    danfeData, 
    isLoading, 
    error, 
    open, 
    currentOrderId,
    fetchDanfe,
    setOpen
  } = useDanfe();
  const { toast } = useToast();

  // Request DANFE when showDanfe is true and orderId is provided
  useEffect(() => {
    if (showDanfe && orderId) {
      fetchDanfe(orderId);
    }
  }, [showDanfe, orderId, fetchDanfe]);

  // Handle dialog close event
  const handleDanfeClose = (open: boolean) => {
    setOpen(open);
    if (!open && onDanfeClose) {
      onDanfeClose();
    }
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500">
        <p>Nenhum produto adicionado</p>
        <p className="text-xs mt-1">Adicione produtos para iniciar a venda</p>
      </div>
    );
  }
  
  return (
    <>
      <ScrollArea className="h-full max-h-[calc(100vh-20rem)]">
        <div className="space-y-3 pr-2">
          {items.map(item => (
            <div 
              key={item.id} 
              className="flex items-center justify-between p-2 border rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.product.name}</p>
                <p className="text-sm text-gray-500 truncate">
                  {formatCurrency(item.product.price)} × {item.quantity} = {formatCurrency(item.product.price * item.quantity)}
                </p>
              </div>
              
              <div className="flex items-center space-x-2 ml-2">
                <div className="flex items-center border rounded-md overflow-hidden">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-8 px-2 rounded-none"
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  
                  <div className="w-8 text-center">
                    <span className="text-sm font-medium">{item.quantity}</span>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-8 px-2 rounded-none"
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onRemoveItem(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <PDVDanfeViewer
        open={open}
        onOpenChange={handleDanfeClose}
        danfeData={danfeData}
        isLoading={isLoading}
        error={error}
        orderId={currentOrderId}
      />
    </>
  );
};

export default PDVCart;
